import { createBaseStore, BaseStoreState, StoreActions } from '@tasama/utils';
import { i18n } from '@tasama/i18n';
import { resetBusinessAdvisorConversation } from '@tasama/business-advisor';
import { apiClient, queryClient } from '../config/api-config';
import { clearUserContextCache } from '../services';
import { createStableBrowserFingerprint } from '../utils/security-utils';
import { clearAllPersistedStores } from './session-cleanup';
import type {
  AuthUser,
  LoginFormData,
  LoginResponse,
  ApiUser,
} from '../schemas/auth-schemas';

/**
 * Authentication store using Zustand (in-memory only).
 *
 * Auth state is derived entirely from the server:
 * - On login: user object is populated from the POST /auth/login response body.
 * - On page refresh / app init: AuthProvider calls checkAuthStatus() which hits
 *   GET /auth/me. If the httpOnly cookie is still valid the server returns the
 *   user and we mark the session as authenticated; a 401 means no session.
 * - Tokens are never stored in JS memory, localStorage, or sessionStorage.
 *   The browser sends the httpOnly cookies automatically on every request.
 */

// ============================================================================
// HELPERS
// ============================================================================

const transformApiUser = (apiUser: ApiUser): AuthUser => ({
  id: apiUser.sub,
  username: apiUser.username,
  email: apiUser.email,
  isAdmin: apiUser.isAdmin ?? false,
});

// ============================================================================
// STATE SHAPE
// ============================================================================

interface AuthStateData {
  user: AuthUser | null;
  isAuthenticated: boolean;
  errorType: 'login' | 'unauthorized' | 'network' | 'general' | null;
  /** True once checkAuthStatus has resolved at least once (success or failure). */
  authChecked: boolean;
}

interface AuthStateActions {
  login: (_credentials: LoginFormData) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  setLoading: (_loading: boolean) => void;
  setError: (
    _error: string | null,
    _type?: 'login' | 'unauthorized' | 'network' | 'general'
  ) => void;
  checkAuthStatus: () => Promise<void>;
  handleUnauthorized: () => void;
  setUser: (_user: AuthUser | null) => void;
  clearSession: () => void;
}

type AuthStoreState = BaseStoreState & AuthStateData & AuthStateActions;
export type AuthState = AuthStoreState & StoreActions;

// ============================================================================
// STORE
// ============================================================================

export const useAuthStore = createBaseStore<AuthStoreState>(
  {
    user: null,
    isAuthenticated: false,
    errorType: null,
    authChecked: false,
  } as Omit<AuthStoreState, keyof BaseStoreState>,
  (set, get) => ({
    // ------------------------------------------------------------------
    // login — POST /auth/login
    // Cookies are set by the BFF. We only persist user info in memory.
    // ------------------------------------------------------------------
    login: async (credentials: LoginFormData) => {
      try {
        set({ error: null });

        const response = await apiClient.axios.post<LoginResponse>(
          '/auth/login',
          {
            username: credentials.email,
            password: credentials.password,
            fingerprint: createStableBrowserFingerprint(),
          }
        );

        if (!response?.data?.user) {
          throw new Error('Invalid response from server');
        }

        const user = transformApiUser(response.data.user);

        resetBusinessAdvisorConversation();

        set({
          user,
          isAuthenticated: true,
          error: null,
          errorType: null,
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : i18n.t('errors.loginFailed', { ns: 'auth' });
        set({
          error: errorMessage,
          errorType: 'login',
          user: null,
          isAuthenticated: false,
        });
        throw error;
      }
    },

    // ------------------------------------------------------------------
    // logout — POST /auth/logout
    // BFF clears the httpOnly cookies. We clear local app state.
    // ------------------------------------------------------------------
    logout: async () => {
      // Cancel in-flight requests before logout POST so they don't reach BFF
      // after the session is gone. cancelAll() resets the controller so the
      // logout POST itself is not aborted.
      apiClient.cancelAll();
      try {
        if (get().isAuthenticated) {
          await apiClient.axios.post('/auth/logout');
        }
      } catch {
        // Continue clearing local state even if the API call fails
      } finally {
        get().clearSession();
      }
    },

    // ------------------------------------------------------------------
    // checkAuthStatus — called on every app load / page refresh.
    // Calls GET /auth/me with credentials (cookie sent automatically).
    // 200 → session is active, populate user state.
    // 401 → no active session, ensure state is cleared.
    // ------------------------------------------------------------------
    checkAuthStatus: async () => {
      // Skip if we already know we're authenticated (e.g. right after login),
      // if a check is already in-flight (prevents React StrictMode double-fire),
      // or if a check has already completed this session (e.g. homeLoader ran first).
      if (get().isAuthenticated || get().loading || get().authChecked) {
        return;
      }

      try {
        set({ loading: true });

        const response = await apiClient.axios.get<{ user: ApiUser }>(
          '/auth/me'
        );

        if (response?.data?.user) {
          const user = transformApiUser(response.data.user);
          set({
            user,
            isAuthenticated: true,
            loading: false,
            authChecked: true,
          });
        } else {
          set({ loading: false, authChecked: true });
        }
      } catch {
        // 401 or network error — no active session
        get().clearSession();
        set({ loading: false, authChecked: true });
      }
    },

    // ------------------------------------------------------------------
    // handleUnauthorized — called by the 401 interceptor when the
    // refresh attempt also fails. Clear local state and let the router
    // redirect to login.
    // ------------------------------------------------------------------
    handleUnauthorized: () => {
      if (!get().isAuthenticated) {
        // 401 on an unauthenticated request — not a session expiry, ignore
        return;
      }

      apiClient.cancelAll();
      // Best-effort logout call so the BFF can clear the cookie server-side
      apiClient.axios.post('/auth/logout').catch(() => {});

      get().clearSession();

      // Restore the session-expiry error after clearSession wipes it
      set({
        error: i18n.t('errors.sessionExpired', { ns: 'auth' }),
        errorType: 'unauthorized',
      });
    },

    // ------------------------------------------------------------------
    // Utility actions
    // ------------------------------------------------------------------
    clearError: () => set({ error: null, errorType: null }),

    setError: (
      error: string | null,
      type: 'login' | 'unauthorized' | 'network' | 'general' = 'general'
    ) => {
      set({ error, errorType: type });
    },

    setLoading: (loading: boolean) => set({ loading }),

    setUser: (user: AuthUser | null) =>
      set({ user, isAuthenticated: user !== null }),

    clearSession: () => {
      resetBusinessAdvisorConversation();
      clearAllPersistedStores();
      clearUserContextCache();
      queryClient.clear();

      set({
        user: null,
        isAuthenticated: false,
        loading: false,
        error: null,
        errorType: null,
        authChecked: false,
      });
    },
  })
);

// ============================================================================
// SELECTORS
// ============================================================================

export const useAuthUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () =>
  useAuthStore((state) => state.isAuthenticated);
export const useAuthLoading = () => useAuthStore((state) => state.loading);
export const useAuthError = () => useAuthStore((state) => state.error);
export const useAuthErrorType = () => useAuthStore((state) => state.errorType);
